import * as React from 'react';
import { Label } from 'reactstrap';
import { Panel, Tooltip } from '@rtkwlf/ui';
import Icon from '../../../../../widgets/Icons';
import { RadioButtons } from '../../../../../widgets/FormikInputFieldV2';
import { useFormikContext } from 'formik';
import {
  SensorFormNetworkValues,
  SensorFormValues,
} from '../../v2/sensor/constants';
import {
  OBSensorNetwork,
  SensorCapability,
  StrictSetFieldValue,
} from '../../../../../types';
import styled from 'styled-components';
import { StandardTooltipOverlay } from '../../../../../constants';
import { useSelector } from 'react-redux';
import {
  featureFlagSelector,
  sessionSelector,
} from '../../../../../reducers/session';

export const HardwareConfigPanel: typeof Panel = styled(Panel)`
  margin-left: 0px;
  margin-right: 0px;
  margin-top: 20px;
`;

const SingleOptionLabel = styled.span`
  font-size: 0.9rem;
  margin-left: 0.8rem;
`;
SingleOptionLabel.displayName = 'SingleOptionLabel';

const CapabilityLabel = styled(Label)`
  margin-top: 0.5rem;
  margin-right: 0.5rem;
  color: black;
`;

CapabilityLabel.displayName = 'CapabilityLabel';

type FormikTypes = SensorFormNetworkValues &
  Pick<SensorFormValues, 'recommendationOverridden'>;

type Props = {
  capabilitySchema: SensorCapability[];
};

const SensorCapabilityForm: React.FunctionComponent<Props> = ({
  capabilitySchema,
}) => {
  const isAwnUser = useSelector(sessionSelector.isAwnUser);
  const { values, setFieldValue } = useFormikContext<FormikTypes>();
  const currentCapabilities = React.useRef(values.currentCapabilities);
  const setStrictFieldValue: StrictSetFieldValue<FormikTypes> = setFieldValue;
  const vanillaSensorEnabled = useSelector(
    featureFlagSelector.onboarding.vanillaSensor,
  );

  React.useEffect(() => {
    // Only runs when capabilitySchema changes
    const caps: OBSensorNetwork['currentCapabilities'] = {};
    if (capabilitySchema) {
      for (const option of capabilitySchema) {
        if (option.options.length === 1) {
          // If the capability only has one option
          caps[option.capabilityType] = option.options[0].value;
        } else if (
          currentCapabilities.current.hasOwnProperty(option.capabilityType)
        ) {
          // Only set values that exist in the schema
          caps[option.capabilityType] =
            currentCapabilities.current[option.capabilityType];
        }
      }
      setStrictFieldValue('currentCapabilities', caps);
    }
  }, [capabilitySchema, setStrictFieldValue]);

  const onCapabilityChange = (name: string, value: string) => {
    const currentCapabilities = {
      ...values.currentCapabilities,
      [name]: value,
    };
    setStrictFieldValue('currentCapabilities', currentCapabilities);
  };

  const filteredList = React.useMemo(() => {
    if (vanillaSensorEnabled) {
      return capabilitySchema;
    } else {
      return capabilitySchema.filter((c) => c.capabilityType !== 'vanilla');
    }
  }, [vanillaSensorEnabled, capabilitySchema]);

  const getCheckedValue = (c: SensorCapability): string => {
    if (
      c.capabilityType === 'vanilla' &&
      !(c.capabilityType in values.currentCapabilities)
    ) {
      return 'false';
    }

    return values.currentCapabilities[c.capabilityType];
  };

  return (
    <HardwareConfigPanel title="Hardware Configuration" titleLevel={5}>
      {filteredList.map((c) => {
        return (
          <div key={c.capabilityType}>
            <div className="d-flex align-items-center">
              <CapabilityLabel className={c.required ? 'required' : ''}>
                {c.label}
              </CapabilityLabel>
              <Tooltip
                placement={'right'}
                overlay={
                  <StandardTooltipOverlay>{c.tooltip}</StandardTooltipOverlay>
                }
              >
                <Icon.Question />
              </Tooltip>
            </div>
            {c.options.length === 1 ? (
              <SingleOptionLabel>{c.options[0].label}</SingleOptionLabel>
            ) : (
              <RadioButtons
                name={c.capabilityType}
                options={c.options}
                className="mb-4"
                onChangeCallback={(name, value) => {
                  onCapabilityChange(name, value);
                }}
                checkedValue={getCheckedValue(c)}
                disabledOptions={
                  values.recommendationOverridden && !isAwnUser
                    ? c.options.map((o) => o.value)
                    : undefined
                }
              />
            )}
          </div>
        );
      })}
    </HardwareConfigPanel>
  );
};

export default SensorCapabilityForm;
